import { Dumbbell, Sparkles, Hourglass, Activity, Flame, Brain, Zap, Moon, Shield, Heart, Droplets, Leaf, Sun, Coffee } from 'lucide-react';

// The /copy suffix makes Google Sheets prompt "Make a copy" the instant anyone opens this
// link, rather than relying on the Read Me tab's manual "File > Make a copy" instruction —
// protects the master template from accidental edits by users of the app.
export const STACK_IMPORT_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/1g2UeWKY3bVWM19wENWL1tH2nOb0hjXV8qI5Qj0rBnR4/copy';

// Compound goal metadata — used by CompoundIcon, CompoundForm
export const GOALS = {
  energy:    { label: 'Energy',    Icon: Zap,      color: '#00d8f0' },
  beauty:    { label: 'Beauty',    Icon: Sparkles,  color: '#f0a030' },
  longevity: { label: 'Longevity', Icon: Hourglass, color: '#a060f0' },
  recovery:  { label: 'Recovery',  Icon: Activity,  color: '#40c890' },
  weight:    { label: 'Weight',    Icon: Flame,     color: '#ff6030' },
  brain:     { label: 'Brain',     Icon: Brain,     color: '#6090ff' },
  strength:  { label: 'Strength',  Icon: Dumbbell,  color: '#60d060' },
  sleep:     { label: 'Sleep',     Icon: Moon,      color: '#8060c0' },
  immunity:  { label: 'Immunity',  Icon: Shield,    color: '#30c8a0' },
  heart:     { label: 'Heart',     Icon: Heart,     color: '#f06080' },
  vitality:  { label: 'Vitality',  Icon: Droplets,  color: '#40b8f0' },
  gut:       { label: 'Gut',       Icon: Leaf,      color: '#80c840' },
};

export const PALETTE = [
  { color: '#00d8f0', dim: '#002830', text: '#c0f4fa' },
  { color: '#f26b3b', dim: '#401808', text: '#f8d0b0' },
  { color: '#4ab868', dim: '#0e3020', text: '#c0ecd0' },
  { color: '#5888e8', dim: '#101840', text: '#c0d0f8' },
  { color: '#e85880', dim: '#400818', text: '#f8c0d0' },
  { color: '#9060d8', dim: '#201040', text: '#d8c0f8' },
  { color: '#d8a030', dim: '#402800', text: '#f8e0b0' },
  { color: '#e87060', dim: '#400e08', text: '#f8c8b8' },
  { color: '#30d4a0', dim: '#083828', text: '#b0f8e8' },
  { color: '#7890a8', dim: '#182028', text: '#d0dce8' },
];

export const ALL_ZONES = [
  { id: 'upper_abd', label: 'Upper Abdomen', positions: ['L', 'Mid', 'R'] },
  { id: 'lower_abd', label: 'Lower Abdomen', positions: ['L', 'Mid', 'R'] },
  { id: 'thigh',     label: 'Thigh',         positions: ['L', 'R'] },
  { id: 'arm',       label: 'Arm',           positions: ['L', 'R'] },
  { id: 'glute',     label: 'Glute',         positions: ['L', 'R'] },
];

export const POS_ICON = { L: '←', Mid: '·', R: '→' };

export const TRACKER_MOOD_LEVELS = [
  { value: 1, emoji: '😔', label: 'Low',     color: '#5878c8' },
  { value: 2, emoji: '😐', label: 'Okay',    color: '#7890a8' },
  { value: 3, emoji: '🙂', label: 'Good',    color: '#d8c030' },
  { value: 4, emoji: '😄', label: 'Great',   color: '#4ab868' },
  { value: 5, emoji: '🤩', label: 'Amazing', color: '#f26b3b' },
];

export const MILESTONES_SHOTS  = [1, 10, 25, 50, 100, 250, 500];
export const MILESTONES_STREAK = [7, 14, 30, 60, 90, 180, 365];

export const ACHIEVEMENT_BADGES = [
  { id: 'first_stick',      emoji: '💉', label: 'FIRST STICK',      sub: 'Logged your first injection',            color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'century',          emoji: '🎯', label: 'CENTURY',          sub: '100 injections logged',                  color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'double_century',   emoji: '🚀', label: 'DOUBLE CENTURY',   sub: '200 injections logged',                  color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'five_hundred',     emoji: '⚡', label: 'FIVE HUNDRED',     sub: '500 injections logged',                  color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'first_pill',           emoji: '💊', label: 'FIRST PILL',           sub: 'Logged your first pill dose',            color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'pill_century',         emoji: '💊', label: 'PILL CENTURY',         sub: '100 pill doses logged',                  color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'pill_double_century',  emoji: '💊', label: 'PILL DOUBLE CENTURY',  sub: '200 pill doses logged',                  color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'pill_five_hundred',    emoji: '💊', label: 'PILL FIVE HUNDRED',    sub: '500 pill doses logged',                  color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'trend_tracker',    emoji: '📊', label: 'TREND TRACKER',    sub: '5+ check-ins — trends unlocked',         color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'data_devotee',     emoji: '🧬', label: 'DATA DEVOTEE',     sub: '25+ check-ins logged',                   color: '#9060d8', bg: 'rgba(144,96,216,0.06)',   border: 'rgba(144,96,216,0.22)' },
  { id: 'dropping',          emoji: '📉', label: 'DROPPING',          sub: '1+ lb / 0.5+ kg from peak',              color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'five_down',         emoji: '🥈', label: 'FIVE DOWN',         sub: '5+ lbs / 2.5+ kg from peak',             color: '#7890a8', bg: 'rgba(120,144,168,0.06)',  border: 'rgba(120,144,168,0.22)' },
  { id: 'ten_down',          emoji: '🥇', label: 'TEN DOWN',          sub: '10+ lbs / 5+ kg from peak',              color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'fifteen_down',      emoji: '🏆', label: 'FIFTEEN DOWN',      sub: '15+ lbs / 7+ kg from peak',              color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'twenty_down',       emoji: '👑', label: 'TWENTY DOWN',       sub: '20+ lbs / 9+ kg from peak',              color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'fifty_down',        emoji: '💫', label: 'FIFTY DOWN',        sub: '50+ lbs / 22.5+ kg from peak',           color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'seventy_five_down', emoji: '🔥', label: 'SEVENTY-FIVE DOWN', sub: '75+ lbs / 34+ kg from peak',             color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'hundred_down',      emoji: '🌟', label: 'ONE HUNDRED DOWN',  sub: '100+ lbs / 45+ kg from peak',            color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'gaining',           emoji: '📈', label: 'GAINING',           sub: '1+ lb / 0.5+ kg from trough',            color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'five_up',           emoji: '💪', label: 'FIVE UP',           sub: '5+ lbs / 2.5+ kg from trough',           color: '#7890a8', bg: 'rgba(120,144,168,0.06)',  border: 'rgba(120,144,168,0.22)' },
  { id: 'ten_up',            emoji: '🏋️', label: 'TEN UP',            sub: '10+ lbs / 5+ kg from trough',            color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'fifteen_up',        emoji: '🔱', label: 'FIFTEEN UP',        sub: '15+ lbs / 7+ kg from trough',            color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'twenty_up',         emoji: '⚡', label: 'TWENTY UP',         sub: '20+ lbs / 9+ kg from trough',            color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'fifty_up',          emoji: '🚀', label: 'FIFTY UP',          sub: '50+ lbs / 22.5+ kg from trough',         color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'compound_curious', emoji: '🧪', label: 'STACK CURIOUS',    sub: 'Tracking 2+ active stack items',         color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'multi_stack',      emoji: '🔬', label: 'MULTI-STACK',      sub: 'Tracking 4+ active stack items',         color: '#e85880', bg: 'rgba(232,88,128,0.06)',   border: 'rgba(232,88,128,0.22)' },
  { id: 'hybrid_protocol',  emoji: '🧩', label: 'HYBRID PROTOCOL',  sub: 'Injectables + pills, together',          color: '#9060d8', bg: 'rgba(144,96,216,0.06)',   border: 'rgba(144,96,216,0.22)' },
  { id: 'week_warrior',     emoji: '⚔️',  label: 'WEEK WARRIOR',     sub: '7-day streak reached',                   color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'month_strong',     emoji: '📅', label: 'MONTH STRONG',     sub: '30-day streak reached',                  color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'quarter',          emoji: '🔰', label: 'QUARTER',          sub: '90-day streak reached',                  color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'annual',           emoji: '🌠', label: 'ANNUAL',           sub: '365-day streak reached',                 color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'protocol_devotee', emoji: '⏱️',  label: 'PROTOCOL DEVOTEE', sub: '1 year of tracking',                     color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'veteran',          emoji: '🎖️',  label: 'VETERAN',          sub: '2 years of tracking',                    color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'site_rotator',     emoji: '🔄', label: 'SITE ROTATOR',     sub: '3+ different injection zones used',      color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'injection_artist', emoji: '🎨', label: 'INJECTION ARTIST', sub: 'Every zone used at least once',          color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'note_taker',       emoji: '📝', label: 'NOTE TAKER',       sub: 'Notes on 25+ shots',                     color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'vial_veteran',     emoji: '⚗️',  label: 'VIAL VETERAN',     sub: '5+ vials emptied',                       color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'early_bird',       emoji: '🌅', label: 'EARLY BIRD',       sub: '10+ shots logged before 8am',            color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'night_owl',        emoji: '🦉', label: 'NIGHT OWL',        sub: '10+ shots logged after 10pm',            color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'pill_week',        emoji: '💊', label: 'PILL WEEK',        sub: '7-day pill streak reached',              color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'pill_month',       emoji: '💊', label: 'PILL MONTH',       sub: '30-day pill streak reached',             color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'pill_quarter',     emoji: '💊', label: 'PILL QUARTER',     sub: '90-day pill streak reached',             color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
  { id: 'pill_year',        emoji: '💊', label: 'PILL YEAR',        sub: '365-day pill streak reached',            color: '#818cf8', bg: 'rgba(129,140,248,0.06)',  border: 'rgba(129,140,248,0.22)' },
  { id: 'first_blend',      emoji: '🥣', label: 'FIRST BLEND',      sub: 'Logged your first blend dose',           color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'blend_master',     emoji: '🧑‍🍳', label: 'BLEND MASTER',     sub: '25+ blend doses logged',                  color: '#f26b3b', bg: 'rgba(242,107,59,0.06)',   border: 'rgba(242,107,59,0.22)' },
  { id: 'back_in_cycle',    emoji: '🔁', label: 'BACK IN CYCLE',    sub: 'Resumed a stack item from a break',      color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'quick_start',      emoji: '⏩', label: 'QUICK START',      sub: 'Imported your stack from a spreadsheet', color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'doctors_orders',   emoji: '🩺', label: "DOCTOR'S ORDERS",  sub: 'Generated a clinician summary report',   color: '#9060d8', bg: 'rgba(144,96,216,0.06)',   border: 'rgba(144,96,216,0.22)' },
  { id: 'total_recall',     emoji: '🧠', label: 'TOTAL RECALL',     sub: 'Handled a recalled vial',                color: '#e84040', bg: 'rgba(232,64,64,0.06)',    border: 'rgba(232,64,64,0.22)' },
  { id: 'lot_keeper',       emoji: '🏷️', label: 'LOT KEEPER',       sub: 'Lot # on 5+ vials or bottles',           color: '#00d8f0', bg: 'rgba(0,216,240,0.06)',    border: 'rgba(0,216,240,0.22)' },
  { id: 'symptom_sleuth',   emoji: '🔍', label: 'SYMPTOM SLEUTH',   sub: 'Symptom Timing found a pattern',         color: '#9060d8', bg: 'rgba(144,96,216,0.06)',   border: 'rgba(144,96,216,0.22)' },
  { id: 'steady_hand',      emoji: '🎯', label: 'STEADY HAND',      sub: '90%+ adherence over 30 days',            color: '#4ab868', bg: 'rgba(74,184,104,0.06)',   border: 'rgba(74,184,104,0.22)' },
  { id: 'pharmacist',       emoji: '💼', label: 'PHARMACIST',       sub: 'Vials from 3+ pharmacies',               color: '#f59e0b', bg: 'rgba(245,158,11,0.06)',   border: 'rgba(245,158,11,0.22)' },
];

export const THEME = {
  dark: {
    BG: '#080c10',
    PANEL: 'rgba(15,18,28,0.8)',
    BORDER: 'rgba(162,128,255,0.1)',
    TEXT: '#d0c8f0',
    DIM: '#5a5070',
    CYAN: '#00d8f0',
    EMBER: '#f26b3b',
  },
  light: {
    BG: '#f0f4f8',
    PANEL: 'rgba(255,255,255,0.95)',
    BORDER: 'rgba(100,80,160,0.15)',
    TEXT: '#1a1a2e',
    DIM: '#7a7a9e',
    CYAN: '#006090',
    EMBER: '#d65a20',
  },
};

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const TIME_OF_DAY_BUCKETS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'bedtime', label: 'Bedtime' },
  { value: null, label: 'Anytime' },
];
export const SHIELD_CYCLE_DAYS = 14;

// A shield only auto-covers a miss discovered within this many days of "today" -- it
// won't reach back and silently "heal" an old, already-accepted streak reset from months
// ago just because shields happen to be available.
export const SHIELD_MISS_WINDOW_DAYS = 7;

// After a Cycle Break closes, a compound isn't held to its reactivated schedule for streak
// purposes until the user logs a dose again -- ending a break is often a planning action
// ("I'm not pausing anymore"), not a same-day commitment to the very next scheduled dose
// (confirmed against production data: a user's break closed Friday, the reactivated
// schedule required Saturday, and they hadn't actually resumed dosing until Sunday -- that
// shouldn't count as a miss at all, let alone cost a shield). Bounded to this many days
// after the break ends so a compound that's genuinely been abandoned post-resume (never
// actually redosed) doesn't sit silently exempt from its schedule forever.
export const RESUME_GRACE_DAYS = 7;

export const DELIVERY_TYPES = [
  { id: 'syringe',      label: 'Syringe',       sub: 'draw from vial · log mL' },
  { id: 'compounded',   label: 'Compounded',     sub: 'draw from vial · log mg' },
  { id: 'pen',          label: 'Pen Injector',   sub: 'dial dose · log mg' },
  { id: 'autoinjector', label: 'Auto-Injector',  sub: 'pre-filled · log mg' },
  { id: 'pill',         label: 'Pills',          sub: 'oral · log mg' },
];

export const TITRATION_PRESETS = {
  semaglutide: {
    label: 'Semaglutide',
    unit: 'mg',
    freq: 'weekly',
    steps: [
      { dose: 0.25, weeks: 4,    label: 'Starting' },
      { dose: 0.5,  weeks: 4,    label: 'Step 2' },
      { dose: 1.0,  weeks: 4,    label: 'Step 3' },
      { dose: 2.0,  weeks: null, label: 'Maintenance' },
    ],
  },
  tirzepatide: {
    label: 'Tirzepatide',
    unit: 'mg',
    freq: 'weekly',
    steps: [
      { dose: 2.5,  weeks: 4,    label: 'Starting' },
      { dose: 5.0,  weeks: 4,    label: 'Step 2' },
      { dose: 7.5,  weeks: 4,    label: 'Step 3' },
      { dose: 10.0, weeks: 4,    label: 'Step 4' },
      { dose: 12.5, weeks: 4,    label: 'Step 5' },
      { dose: 15.0, weeks: null, label: 'Maintenance' },
    ],
  },
  retatrutide: {
    label: 'Retatrutide',
    unit: 'mg',
    freq: 'weekly',
    steps: [
      { dose: 2,  weeks: 4,    label: 'Starting' },
      { dose: 4,  weeks: 4,    label: 'Step 2' },
      { dose: 8,  weeks: 4,    label: 'Step 3' },
      { dose: 12, weeks: null, label: 'Maintenance' },
    ],
  },
  liraglutide: {
    label: 'Liraglutide',
    unit: 'mg',
    freq: 'daily',
    steps: [
      { dose: 0.6, weeks: 1,    label: 'Starting' },
      { dose: 1.2, weeks: 1,    label: 'Step 2' },
      { dose: 1.8, weeks: 1,    label: 'Step 3' },
      { dose: 2.4, weeks: 1,    label: 'Step 4' },
      { dose: 3.0, weeks: null, label: 'Maintenance' },
    ],
  },
};

export const MOOD_LEVELS = {
  1: { label: 'Awful', color: '#dc2626' },
  2: { label: 'Poor', color: '#f97316' },
  3: { label: 'Okay', color: '#eab308' },
  4: { label: 'Good', color: '#84cc16' },
  5: { label: 'Great', color: '#22c55e' },
};

export const HOW_TO_TOPICS = [
  {
    title: 'Logging Your First Dose',
    content: 'Select a stack item from the dashboard, choose your vial or bottle, enter the dose details (date, time, site if injectable, volume or dose), note any side effects, and save. That is it - your data syncs to our servers and stays private.',
  },
  {
    title: 'Managing Your Vault',
    content: 'Add vials or bottles in Settings > Vault. Fill in the stack item, supplier, concentration or pill count, volume or dose, and cost. Track your inventory and we will flag you when supply is running low or past its beyond-use date.',
  },
  {
    title: 'Understanding Streaks & Milestones',
    content: 'Injections and pills each track their own independent streak. You earn 1 shield per 14 days on-schedule (per streak), max 3 banked. Streak milestones unlock at 7, 14, 30, 60, 90, 180, and 365 days, and dose-count milestones unlock at 1, 10, 25, 50, 100, 250, and 500 shots logged - celebrate and share them.',
  },
  {
    title: 'Customizing Your Stack',
    content: 'Add stack items in Settings > Stack. Define frequency (e.g., Sun/Wed/Fri), site if injectable, and dosing details. MyRONKO tracks adherence based on your schedule.',
  },
  {
    title: 'Viewing Your History',
    content: 'History tab shows all logged doses with date, stack item, dose, site, and side effects. Filter by stack item or date range, export as JSON/CSV for backup, or analyze trends.',
  },
  {
    title: 'Understanding Your Dashboard',
    content: 'Overview of your whole stack, current streak status, upcoming doses, low supply alerts, and a quick-add button for todays dose. Everything you need at a glance.',
  },
  {
    title: 'Privacy & Data Ownership',
    content: 'Your data is yours. We never sell it, never show ads, and never share it. Encrypted in transit and at rest. You can export everything anytime.',
  },
  {
    title: 'Sharing Milestone Cards',
    content: 'When you hit a milestone, generate a shareable 4:3 card with your achievement. No personal health details - just streak discipline. Save as image and share on social.',
  },
  {
    title: 'Importing Your Stack',
    content: 'Have an existing spreadsheet of your compounds and inventory? Open the template link in Settings > App Data > Import Stack from Spreadsheet, make your own copy, fill it in, and download each tab as a CSV. Upload all three files together and you will see a preview of what will be created before anything is saved — any rows with issues are listed with the reason, and everything else imports as new stack items and vials/bottles alongside what you already have.',
  },
  {
    title: 'Pausing & Resuming a Stack Item',
    content: "Going off-cycle? Tap the ⋯ menu on a stack item in Settings > Stack and choose Start Break — it's paused without losing your streak or history, and won't count against you while it's on. When you're ready to restart, tap Resume; you'll get a prompt to double check your schedule and vial before your next dose, since your prior frequency applies again right away.",
  },
  {
    title: 'Logging Check-Ins',
    content: 'Track weight, blood glucose, blood pressure, heart rate, mood, and side effects alongside your doses. Blood glucose and weight units follow your Settings > Profile preference, or override per check-in. Trends surfaces a Symptom Timing card once you have enough history, showing which symptoms tend to cluster in the hours after a dose - observational only, never medical advice.',
  },
  {
    title: 'Sharing a Clinician Summary',
    content: "Settings > App Data > Summary generates a printable report for your provider: your protocol, dosing history, adherence per stack item, check-in trends, and (when there's a clear pattern) symptom timing - choose a 30-day, 90-day, or 1-year window. Save as PDF or print directly from the browser tab it opens.",
  },
  {
    title: 'Blends & the Reconstitution Helper',
    content: "Tracking a multi-ingredient mix (e.g. a peptide blend)? Set a default recipe on the stack item and each new vial copies it in - editing the recipe later never changes past vials or shots. For syringe/compounded vials, the Reconstitution Helper in the vial editor turns your vial's mg, added water, and target dose into the concentration, mL, and units to draw.",
  },
  {
    title: 'Understanding Trends',
    content: 'See adherence per stack item, weight/blood pressure/glucose charts, stack volume breakdown, and (for blends) per-ingredient usage over time. Adherence compares your logged doses against whatever schedule was actually active on each day, so past edits or cycle breaks are always judged fairly.',
  },
  {
    title: 'Notifications on Your Devices',
    content: 'Push notifications are turned on per device. In Settings > Profile > Notifications, the toggle shows whether THIS phone or browser gets alerts, and the list below shows every device getting them. Turn it on on each device you want reminders and recall alerts on; turning it off only stops that device. Tap the × next to a device to stop alerts there. On iPhone, add MyRONKO to your Home Screen first.',
  },
  {
    title: 'Recall Alerts',
    content: 'MyRONKO checks FDA recall notices every day and compares them with the pharmacy, stack item and lot # of your vials and bottles. If your lot is on the recall list you will see a red RECALL banner on the Dashboard (and a push notification if notifications are on). If a vial has no lot # recorded, it is shown as a possible match — check your label against the listed lots. Pharmacies often notify customers before FDA posts a recall, so you can also use Mark as recalled in a vial\'s menu yourself. FDA data can lag or be incomplete; this is not medical advice — contact your provider if you have had a reaction.',
  },
];

export const WELCOME_STEPS = [
  {
    title: 'Welcome to MyRONKO',
    subtitle: 'Own Your Protocol.',
    body: 'Privacy-first tracking for your whole stack: peptides, GLP-1s, and vitamins, injectable or oral. Your data is yours. We do not sell it, track you, or show ads.',
  },
  {
    title: 'Set Up Your Protocol',
    subtitle: 'Define what you are tracking.',
    body: 'Add your stack and dosing schedule in Settings. MyRONKO learns your frequency and reminds you when doses are due.',
  },
  {
    title: 'Log & Track',
    subtitle: 'Build your history.',
    body: 'Log each dose with date, time, site if injectable, and side effects. Watch trends emerge and celebrate milestones as you go.',
  },
];

export const DOCTOR_DISCLAIMER = 'MyRONKO is a tracking tool, not medical advice. Always follow your healthcare provider instructions. Report side effects immediately to your doctor.';

export const SIDE_EFFECT_LIST = [
  {label:"Acne",sub:null},
  {label:"Appetite Change",sub:["Increase","Decrease"]},
  {label:"Bloating",sub:null},
  {label:"Brain Fog",sub:null},
  {label:"Cold / Chills",sub:null},
  {label:"Constipation",sub:null},
  {label:"Diarrhea",sub:null},
  {label:"Dizziness",sub:null},
  {label:"Dry Mouth",sub:null},
  {label:"Dry Skin",sub:null},
  {label:"Energy",sub:["High","Low"]},
  {label:"Fatigue",sub:null},
  {label:"Gas",sub:null},
  {label:"Hair Loss",sub:null},
  {label:"Headache",sub:null},
  {label:"Heart Palpitations",sub:null},
  {label:"Hot Flashes",sub:null},
  {label:"Hyperpigmentation",sub:["Increase","Decrease"]},
  {label:"Indigestion",sub:null},
  {label:"Inflammation",sub:["Better","Worse"]},
  {label:"Injection Site Reaction",sub:null},
  {label:"Insomnia",sub:null},
  {label:"Joint Pain",sub:null},
  {label:"Libido Change",sub:["Increase","Decrease"]},
  {label:"Mood Change",sub:["Better","Worse"]},
  {label:"Muscle Aches",sub:null},
  {label:"Nausea",sub:null},
  {label:"Numbness / Tingling",sub:null},
  {label:"Oily Skin",sub:null},
  {label:"Skin Clarity",sub:["Better","Worse"]},
  {label:"Skin Rash / Irritation",sub:null},
  {label:"Sleep Changes",sub:["More","Less"]},
  {label:"Sun Sensitivity",sub:null},
  {label:"Sweating",sub:null},
  {label:"Thirst",sub:null},
  {label:"Vivid Dreams",sub:null},
  {label:"Vomiting",sub:null},
  {label:"Water Retention",sub:null},
  {label:"Weakness",sub:null},
];
